import { useMemo } from 'react'

import Icon from '../icon/Icon'
import {
  ACHIEVEMENT_CATALOG,
  ACHIEVEMENT_ORDER,
} from '../../_defaults/achievement-catalog'
import { useAchievements } from '../../_hooks/use-achievements'
import {
  Section,
  HeaderRow,
  Title,
  Stamp,
  Grid,
  Tile,
  TileGlyph,
  TileTitle,
  TileHint,
  TileStatus,
} from './styled'
import { AchievementsGridProps } from './types'

/**
 * Visual catalog of achievements. Locked tiles render greyed out so the
 * renter sees what they could earn next. Unlocked tiles pop with a soft
 * border + shadow.
 *
 * @param {AchievementsGridProps} props
 * @returns {JSX.Element}
 */
const AchievementsGrid = ({
  showLocked = true,
}: AchievementsGridProps): JSX.Element => {
  const { unlockedIds } = useAchievements()
  const unlockedSet = useMemo(() => new Set(unlockedIds), [unlockedIds])

  const tilesToRender = useMemo(() => {
    if (showLocked === true) {
      return ACHIEVEMENT_ORDER
    }

    return ACHIEVEMENT_ORDER.filter(id => unlockedSet.has(id) === true)
  }, [showLocked, unlockedSet])

  return (
    <Section>
      <HeaderRow>
        <Title>Achievements</Title>
        <Stamp>
          {unlockedIds.length} / {ACHIEVEMENT_ORDER.length} unlocked
        </Stamp>
      </HeaderRow>
      <Grid>
        {tilesToRender.map(id => {
          const descriptor = ACHIEVEMENT_CATALOG[id]
          const isUnlocked = unlockedSet.has(id)

          return (
            <Tile key={id} isUnlocked={isUnlocked}>
              <TileGlyph aria-hidden="true">
                <Icon icon={descriptor.glyph} />
              </TileGlyph>
              <TileTitle>{descriptor.title}</TileTitle>
              <TileHint>{descriptor.hint}</TileHint>
              <TileStatus isUnlocked={isUnlocked}>
                {isUnlocked === true ? 'Unlocked' : 'Locked'}
              </TileStatus>
            </Tile>
          )
        })}
      </Grid>
    </Section>
  )
}

export default AchievementsGrid
